import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface TimeRangePickerProps {
  value: string
  onChange: (value: string) => void
  minTime?: string
  maxTime?: string
  disabled?: boolean
  className?: string
  startAdornment?: string
  error?: boolean
}

export function TimeRangePicker({
  value,
  onChange,
  minTime,
  maxTime,
  disabled = false,
  className,
  startAdornment,
  error = false,
}: TimeRangePickerProps) {
  const generateTimeOptions = () => {
    const options: string[] = []
    const minTimeMinutes = minTime ? parseTime(minTime) : -1
    const maxTimeMinutes = maxTime ? parseTime(maxTime) : 24 * 60 + 1

    // Generate all 15-minute intervals from 00:00 to 24:00
    for (let totalMinutes = 0; totalMinutes <= 24 * 60; totalMinutes += 15) {
      const hour = Math.floor(totalMinutes / 60)
      const minute = totalMinutes % 60

      // Format as HH:MM (24:00 for end of day)
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      const timeMinutes = totalMinutes

      if (timeMinutes > minTimeMinutes && timeMinutes < maxTimeMinutes) {
        options.push(timeString)
      }
    }

    return options
  }

  const parseTime = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
  }

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number)

    // Handle 24:00 as 12:00 AM (end of day)
    if (hours === 24) {
      return `12:${minutes.toString().padStart(2, '0')} AM`
    }

    // Handle 00:00 as 12:00 AM (start of day)
    if (hours === 0) {
      return `12:${minutes.toString().padStart(2, '0')} AM`
    }

    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours > 12 ? hours - 12 : hours
    const displayMinutes = minutes.toString().padStart(2, '0')
    return `${displayHours}:${displayMinutes} ${period}`
  }

  const timeOptions = generateTimeOptions()

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={`${className} ${error ? 'bg-error-subtle border-error-border text-error' : ''}`}
      >
        <SelectValue placeholder="Select time">
          {startAdornment && (
            <span
              className={`text-xs mr-1 ${error ? 'text-error-emphasis' : 'text-muted-foreground'}`}
            >
              {startAdornment}
            </span>
          )}
          <span className={`w-16 text-left ${error ? 'text-error' : ''}`}>
            {value && formatTime(value)}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-60">
        {timeOptions.map(time => (
          <SelectItem key={time} value={time}>
            {formatTime(time)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
